<template>
  <canvas ref="canvasRef" class="hero-canvas"></canvas>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const canvasRef = ref(null)
let ctx = null
let animationFrame = null
let width = 1000 // Safe default
let height = 800
let mouseX = 0
let mouseY = 0
let targetMouseX = 0
let targetMouseY = 0

// Configuration
const RINGS = 30
const SECTORS = 40
const BASE_RADIUS = 50

// Read theme colors from CSS variables (fallback to cyan)
let primaryRGB = [0, 242, 255]
let secondaryRGB = [255, 107, 107]

function readThemeColors() {
    if (typeof document === 'undefined') return
    const style = getComputedStyle(document.documentElement)
    const pChannels = style.getPropertyValue('--primary-channels').trim()
    const sChannels = style.getPropertyValue('--secondary-channels').trim()
    if (pChannels) primaryRGB = pChannels.split(' ').map(Number)
    if (sChannels) secondaryRGB = sChannels.split(' ').map(Number)
}

// State
let time = 0
const points = [] // Array of { angle, radius, x, y, z, originalZ, rIndex, sIndex }

const init = () => {
    if (!canvasRef.value) return
    ctx = canvasRef.value.getContext('2d')
    readThemeColors()
    resize()
    
    // Initialize Polar Grid
    points.length = 0
    for (let r = 0; r < RINGS; r++) {
        const radiusRatio = r / (RINGS - 1)
        // Responsive max radius
        const maxR = (width > 800) ? 600 : (width / 2 - 20)
        const radius = BASE_RADIUS + radiusRatio * maxR
        
        for (let s = 0; s < SECTORS; s++) {
            const angle = (s / SECTORS) * Math.PI * 2
            points.push({
                rIndex: r,
                sIndex: s,
                angle,
                radius,
                baseRadius: radius,
                z: 0
            })
        }
    }
}

const resize = () => {
    if (typeof window === 'undefined') return
    if (!canvasRef.value) return
    width = window.innerWidth
    height = window.innerHeight
    canvasRef.value.width = width
    canvasRef.value.height = height
}

const project = (point) => {
    // 3D Tilt Transform
    const x = Math.cos(point.angle) * point.radius
    const y = Math.sin(point.angle) * point.radius
    const z = point.z
    
    // Tilt the "disk" back
    const tilt = 0.6 // radians
    const yTilt = y * Math.cos(tilt) - z * Math.sin(tilt)
    const zTilt = y * Math.sin(tilt) + z * Math.cos(tilt)
    
    // Project to 2D
    const fov = 600
    // Prevent divide by zero or negative scale behind camera
    const dist = fov - zTilt + 400
    const scale = (dist > 1) ? fov / dist : 0
    
    return {
        x: width/2 + x * scale,
        y: height/2 + yTilt * scale - 40,
        scale,
        alpha: Math.min(1, Math.max(0, scale))
    }
}

const update = () => {
    // Smooth mouse interaction
    const centerX = width/2
    const centerY = height/2
    
    // Approximate mouse pos relative to center
    const mouseDX = mouseX - centerX
    const mouseDY = mouseY - centerY
    
    time += 0.02
    
    for (let i = 0; i < points.length; i++) {
        const p = points[i]
        
        // 1. Base Speaker "Thump"
        const thump = Math.sin(time * 2) * Math.exp(-p.rIndex * 0.2) * 20
        
        // 2. Ripple Wave
        const ripple = Math.sin(p.radius * 0.05 - time * 3) * 15
        
        // 3. Mouse Interaction
        // Project point loosely to 2D for distance check (optimization: just use radius/angle)
        const px = Math.cos(p.angle) * p.radius
        const py = Math.sin(p.angle) * p.radius * 0.5 // foreshortening
        
        const distToMouse = Math.hypot(px - mouseDX, py - mouseDY)
        const mouseForce = Math.exp(-distToMouse / 150) * 80 * Math.sin(time * 10)
        
        p.z = thump + ripple + mouseForce
    }
}

const draw = () => {
    if (!ctx) return
    ctx.clearRect(0, 0, width, height)
    ctx.lineCap = 'round'
    
    // Convert all to 2D first
    // Optimization: Pre-allocate array or map
    const projPoints = points.map(p => {
        const proj = project(p)
        return { ...p, ...proj }
    })
    
    // Draw Rings
    for (let r = 0; r < RINGS; r++) {
         const ringPoints = projPoints.filter(p => p.rIndex === r)
         if (ringPoints.length < 2) continue

         ctx.beginPath()
         
         const ratio = r / RINGS
         // Lerp from primary (inner) to secondary (outer)
         const rVal = primaryRGB[0] + ratio * (secondaryRGB[0] - primaryRGB[0])
         const gVal = primaryRGB[1] + ratio * (secondaryRGB[1] - primaryRGB[1])
         const bVal = primaryRGB[2] + ratio * (secondaryRGB[2] - primaryRGB[2])

         ctx.strokeStyle = `rgba(${Math.round(rVal)}, ${Math.round(gVal)}, ${Math.round(bVal)}, ${0.6 - ratio*0.4})`
         ctx.lineWidth = 2
         
         // Closed loop
         ctx.moveTo(ringPoints[0].x, ringPoints[0].y)
         for (let s = 1; s < ringPoints.length; s++) {
             ctx.lineTo(ringPoints[s].x, ringPoints[s].y)
         }
         ctx.closePath() // Connect last to first
         ctx.stroke()
    }
    
    // Draw Spokes
    for (let s = 0; s < SECTORS; s += 2) {
        ctx.beginPath()
        ctx.strokeStyle = `rgba(${primaryRGB[0]}, ${primaryRGB[1]}, ${primaryRGB[2]}, 0.1)`
        ctx.lineWidth = 1
        
        // Extract column
        // Points are pushed RINGS then SECTORS
        // points[0] = r0,s0. points[1] = r0,s1 ...
        // Index = r * SECTORS + s
        
        let first = true
        for (let r = 0; r < RINGS; r++) {
            const idx = r * SECTORS + s
            if (idx >= projPoints.length) continue
            
            const p = projPoints[idx]
            if (p.scale <= 0) continue

            if (first) {
                ctx.moveTo(p.x, p.y)
                first = false
            } else {
                ctx.lineTo(p.x, p.y)
            }
        }
        ctx.stroke()
    }
}

const animate = () => {
  update()
  draw()
  animationFrame = requestAnimationFrame(animate)
}

const handleMouseMove = (e) => {
  if (!canvasRef.value) return 
  const rect = canvasRef.value.getBoundingClientRect()
  targetMouseX = e.clientX - rect.left
  targetMouseY = e.clientY - rect.top
  // Smooth lerp
  mouseX += (targetMouseX - mouseX) * 0.2
  mouseY += (targetMouseY - mouseY) * 0.2
}

onMounted(() => {
  init()
  window.addEventListener('resize', resize)
  // Respect reduced-motion: paint a single static frame and stop, rather than
  // spinning a permanent requestAnimationFrame loop (a needless CPU/battery
  // drain, felt most on phones). Motion-allowed visitors get the live field.
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduce) {
    animate()
    if (animationFrame) { cancelAnimationFrame(animationFrame); animationFrame = null }
    return
  }
  window.addEventListener('mousemove', handleMouseMove)
  animationFrame = requestAnimationFrame(animate)
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', handleMouseMove)
  }
  if (animationFrame) cancelAnimationFrame(animationFrame)
})
</script>

<style scoped>
.hero-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 105%;
  pointer-events: none;
  z-index: 2;
  mix-blend-mode: screen;
}
</style>
